/**
 * Dashboard Router Component
 * Routes users to role-appropriate dashboards
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useWorkspace } from '@/features/workspace-sub-delegation';
import { Spinner } from '@/shared/components/Loading/Spinner';
import type { UserRole } from '@/core/auth/types';

const SUPPORT_FORM_URL = import.meta.env.VITE_SUPPORT_FORM_URL;

const DASHBOARD_BY_ROLE: Partial<Record<UserRole, string>> = {
  CARE_SEEKER: '/dashboard/care-seeker',
  CARE_RECIPIENT: '/dashboard/care-seeker',
  SERVICE_PROVIDER: '/dashboard/service-provider',
  FINANCIAL_ADVISOR: '/dashboard/financial-advisor',
  ADMIN: '/dashboard/admin',
};

const LoadingScreen = () => (
  <div className="flex h-screen items-center justify-center bg-background">
    <div className="text-center">
      <Spinner className="mx-auto mb-4" />
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

export function DashboardRouter() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Feature 075: a SUB_DELEGATE lands on the dashboard of the workspace
  // owner's role (effective role, D11).
  if (user.role === 'SUB_DELEGATE') {
    return <SubDelegateDashboardRedirect onLogout={logout} />;
  }

  const target = DASHBOARD_BY_ROLE[user.role];
  if (target) {
    return <Navigate to={target} replace />;
  }

  return <DashboardRoleError onLogout={logout} />;
}

function SubDelegateDashboardRedirect({ onLogout }: { onLogout: () => void }) {
  const { isLoading, activeWorkspace, workspaces } = useWorkspace();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (workspaces.length === 0 || !activeWorkspace) {
    return <NoWorkspacesScreen onLogout={onLogout} />;
  }

  const target = DASHBOARD_BY_ROLE[activeWorkspace.ownerRole];
  if (target) {
    return <Navigate to={target} replace />;
  }

  return <DashboardRoleError onLogout={onLogout} />;
}

function NoWorkspacesScreen({ onLogout }: { onLogout: () => void }) {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div
        className="max-w-md text-center p-8 bg-white rounded-xl shadow-lg"
        data-testid="no-workspaces-screen"
      >
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#026857]/10">
          <svg className="h-6 w-6 text-[#026857]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
          </svg>
        </div>

        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          No active workspaces
        </h1>
        <p className="text-gray-600 mb-6 text-sm">
          You don't have access to any workspace right now. Ask the workspace
          owner to send you a new invitation.
        </p>

        <button
          onClick={onLogout}
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function DashboardRoleError({ onLogout }: { onLogout: () => void }) {
  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="max-w-md text-center p-8 bg-white rounded-xl shadow-lg">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
          <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
        </div>

        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-600 mb-1">
          We couldn't load your account settings.
        </p>
        <p className="text-gray-500 text-sm mb-6">
          This is usually temporary. Try refreshing the page.
        </p>

        <div className="flex flex-col gap-3">
          <button
            onClick={handleRetry}
            className="w-full rounded-lg bg-[#026857] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#025246] transition-colors"
          >
            Refresh page
          </button>
          <button
            onClick={onLogout}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Sign out and try again
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-6">
          If this keeps happening,{' '}
          <button
            onClick={() => {
              const w = window.open(SUPPORT_FORM_URL, '_blank', 'noopener,noreferrer');
              if (w) w.opener = null;
            }}
            className="underline hover:text-gray-600 transition-colors"
          >
            contact support
          </button>
        </p>
      </div>
    </div>
  );
}

